import { DebugButton } from '@cibo/profile'
import {
  ResourceDetailFeatureTaskEditorProps,
  downloadAxiosResponse,
  logRequestError,
} from '@cibo/ui'
import { LoadingButton } from '@mui/lab'
import { Box, Card, Chip, FormHelperText, Grid, Stack, Typography } from '@mui/material'
import { AxiosError } from 'axios'
import { useSnackbar } from 'notistack'
import { propEq } from 'ramda'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import {
  useProgramEngagementDetails,
  useUpdateProgramEngagementDetails,
} from '../../../../hooks/useProgramEngagementDetails'
import { RDEqipAutogenRequiredForms } from '../RDEqipAutogenRequiredFormsResourceDetailFeature/types'
import { RequiredDocumentsAPI } from '../RequiredDocumentsAPI'
import { RDEqipDocuments } from './types'
import { useProcessSigningReturn } from './useProcessSigningReturn'

export const RDEqipDocumentsTaskEditor = ({
  detailRequirements,
  engagementId,
  resourceIds: [resourceId],
}: ResourceDetailFeatureTaskEditorProps<RDEqipDocuments>) => {
  const { t } = useTranslation('@cibo/programs/RDEqipDocumentsTaskEditor')
  const { enqueueSnackbar } = useSnackbar()

  const details = useProgramEngagementDetails({
    detailRequirements,
    resourceId: engagementId,
  })

  const detail = details.data?.find(propEq('traitId', 'eqipDocuments'))

  const updateDetail = useUpdateProgramEngagementDetails()

  const [envelopeLoading, setEnvelopLoading] = useState(false)
  useEffect(() => {
    if (!engagementId) return
    setEnvelopLoading(true)
    RequiredDocumentsAPI.generate(engagementId)
      .catch((error: AxiosError) => {
        enqueueSnackbar(error.message, { variant: 'error' })
        logRequestError(error)
      })
      .finally(() => {
        setEnvelopLoading(false)
      })
  }, [])

  const processSigningReturn = useProcessSigningReturn()

  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    if (searchParams.has('event')) {
      const eventParam = searchParams.get('event') as string

      searchParams.delete('event')
      setSearchParams(searchParams)

      processSigningReturn.mutateAsync({ engagementId, eventParam })
    }
  }, [searchParams])

  const [loadingDocusignUrl, setLoadingDocusignUrl] = useState(false)
  const [loadingDownloadBlankForms, setLoadingDownloadBlankForms] = useState(false)

  const onDownloadBlankForms = useCallback(() => {
    setLoadingDownloadBlankForms(true)

    RequiredDocumentsAPI.download(engagementId)
      .then(downloadAxiosResponse())
      .catch((error: Error) => {
        enqueueSnackbar(error.message, { variant: 'error' })
      })
      .finally(() => {
        setLoadingDownloadBlankForms(false)
      })

    updateDetail.mutateAsync({
      resourceId,
      details: [
        {
          traitId: 'eqipAutogenRequiredForms',
          // @todo get a better year. the happenstance first requirement may not be yearly
          year: detailRequirements[0].year,
          result: { allRequiredDocumentsDownloaded: true },
        } as RDEqipAutogenRequiredForms,
      ],
    })
  }, [])

  const onClickSign = useCallback(() => {
    if (!engagementId) return
    setLoadingDocusignUrl(true)

    RequiredDocumentsAPI.url(engagementId, window.location.href)
      .then((response: { docusignUrl: string }) => (document.location.href = response.docusignUrl))
      .catch((error: AxiosError) => {
        enqueueSnackbar(error.message, { variant: 'error' })
        logRequestError(error)
      })
      .finally(() => {
        setLoadingDocusignUrl(false)
      })
  }, [])

  return (
    <Stack spacing={3}>
      <Grid container columns={2}>
        <Grid item xs={1}>
          <Card sx={{ p: 2, mr: 2 }}>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Stack>
                  <Typography variant="overline">{t('option1')}</Typography>
                  <Typography variant="body1">{t('eSign')}</Typography>
                </Stack>

                <Chip color="success" label={t('recommended')} size="small" variant="outlined" />
              </Stack>

              <Typography variant="body2">{t('option1desc')}</Typography>

              <Box mt={2}>
                <LoadingButton
                  variant="contained"
                  onClick={onClickSign}
                  loading={envelopeLoading || loadingDocusignUrl}
                  data-testid="view-documents-button"
                >
                  {t('reviewAndSign')}
                </LoadingButton>
              </Box>
            </Stack>
          </Card>

          <DebugButton
            loading={updateDetail.isLoading}
            disabled={!detail}
            onClick={() => {
              updateDetail.mutateAsync({
                resourceId,
                details: [
                  {
                    traitId: 'eqipDocuments',
                    // @todo get a better year. the happenstance first requirement may not be yearly
                    year: detailRequirements[0].year,
                  } as RDEqipAutogenRequiredForms,
                ],
              })
            }}
          >
            Reset Docusign
          </DebugButton>
        </Grid>

        <Grid item xs={1}>
          <Card sx={{ p: 2 }}>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Stack>
                  <Typography variant="overline">{t('option2')}</Typography>
                  <Typography variant="body1">{t('completeOffline')}</Typography>
                </Stack>
              </Stack>

              <Typography variant="body2">{t('option2desc')}</Typography>

              <Box mt={2}>
                <LoadingButton
                  variant="contained"
                  onClick={onDownloadBlankForms}
                  loading={envelopeLoading || loadingDownloadBlankForms}
                  data-testid="download-blank-forms-button"
                >
                  {t('downloadBlankForms')}
                </LoadingButton>
              </Box>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      <FormHelperText>{t('disclaimer')}</FormHelperText>
    </Stack>
  )
}
